// angular
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Params } from '@angular/router';
// components
import { MessageDialogComponent } from '@app/core/dialogs/message-dialog/message-dialog.component';
// services
import { PwjToastService } from '@app/core/shared/services/app-toast.service';
import { DialogService } from '@app/core/shared/services/dialog.service';
// types
import {
  QuickOrderDetailsCard,
  QuickOrderDetailsCardList,
  QuickOrderFormType,
  IQuickOrder,
  QuickOrdersListResponse,
} from '../types/quick-order.type';

@Injectable()
export class QuickOrderService {
  // states
  public orderSubmitState = signal<boolean>(false);
  public orderSubmitFailed = signal<boolean>(false);
  public acceptOrderState = signal<boolean>(false);

  // home orders data
  public homeOrdersData = signal<IQuickOrder[] | null>(null);
  public homeOrdersDataCount = signal<number>(0);
  public isHomeOrdersLoading = signal<boolean>(false);

  // orders list data
  public ordersListData = signal<QuickOrderDetailsCard[] | null>(null);
  public ordersDataCount = signal<number>(0);
  public isOrdersListDataLoading = signal<boolean>(false);

  // quick order details data
  public quickOrderDetails = signal<QuickOrderDetailsCard | null>(null);
  public isQuickOrderDetailsLoading = signal<boolean>(false);

  // dependencies
  private readonly _httpClient = inject(HttpClient);
  private readonly _toast = inject(PwjToastService);
  private readonly _dialogService = inject(DialogService);

  constructor() {}

  private submitOrder(data: QuickOrderFormType) {
    return this._httpClient.post<IQuickOrder>(`main/quick-order`, data);
  }

  public postQuickOrder(data: QuickOrderFormType) {
    // open successfully message dialog after order submitted
    this.submitOrder(data).subscribe({
      next: res => {
        const dialogRef = this._dialogService.open(MessageDialogComponent, {
          width: '23em',
          height: 'none',
          data: {
            title: 'toastMessages.submitOrder',
            message: 'global.trackingCode',
            closeButtonText: 'global.submit',
            messageType: 'SUCCESS',
            code: res.code,
          },
        });

        dialogRef.afterClosed().subscribe(res => {
          this.orderSubmitState.set(true);
          this.orderSubmitFailed.set(false);
        });
      },
      error: error => {
        // auth error message (toast)
        if (error.status === 401) {
          this._toast.error(
            'toastMessages.loginMessage',
            'toastMessages.loginTitle'
          );
        } else {
          this._toast.error('toastMessages.error', 'toastMessages.errorTitle');
        }

        this.orderSubmitState.set(false);
        this.orderSubmitFailed.set(true);
      },
      complete: () => null,
    });
  }

  public getHomeQuickOrders() {
    // set loading state
    this.isHomeOrdersLoading.set(true);
    return this._httpClient
      .get<QuickOrdersListResponse>(`main/quick-order`, {
        params: {
          take: 8,
          skip: 0,
        },
      })
      .subscribe({
        next: res => {
          this.homeOrdersData.set(res.data);
          this.homeOrdersDataCount.set(res.count);
        },
        error: () => {
          this.isHomeOrdersLoading.set(false);
        },
        complete: () => {
          this.isHomeOrdersLoading.set(false);
        },
      });
  }

  public getQuickOrdersListData(params?: Params) {
    // set loading state
    this.isOrdersListDataLoading.set(true);
    return this._httpClient
      .get<QuickOrderDetailsCardList>(`main/quick-order`, {
        params,
      })
      .subscribe({
        next: res => {
          this.ordersListData.set(res.data);
          this.ordersDataCount.set(res.count);
        },
        error: () => {
          this.isOrdersListDataLoading.set(false);
        },
        complete: () => {
          this.isOrdersListDataLoading.set(false);
        },
      });
  }

  public getQuickOrderDetailsById(id: string) {
    // set loading state
    this.isQuickOrderDetailsLoading.set(true);
    return this._httpClient
      .get<QuickOrderDetailsCard>(`main/quick-order/${id}`)
      .subscribe({
        next: res => {
          this.quickOrderDetails.set(res);
        },
        error: () => {
          this.isQuickOrderDetailsLoading.set(false);
        },
        complete: () => {
          this.isQuickOrderDetailsLoading.set(false);
        },
      });
  }

  private submitAcceptOrderRequest(data: any) {
    return this._httpClient.post<IQuickOrder>(
      `main/quick-order-response`,
      data
    );
  }

  public submitAcceptOrderData(data: any) {
    // open successfully message dialog after order submitted
    this.submitAcceptOrderRequest(data).subscribe({
      next: res => {
        const dialogRef = this._dialogService.open(MessageDialogComponent, {
          width: '23em',
          height: 'none',
          data: {
            title: 'toastMessages.acceptOrderRequestSubmitted',
            message: 'toastMessages.trackRequest',
            closeButtonText: 'global.submit',
            messageType: 'SUCCESS',
            code: res.code,
          },
        });

        dialogRef.afterClosed().subscribe(res => {
          this.acceptOrderState.set(true);
        });
      },
      error: error => {
        // auth error message (toast)
        if (error.status === 401) {
          this._toast.error(
            'toastMessages.loginMessage',
            'toastMessages.loginTitle'
          );
        } else {
          this._toast.error('toastMessages.error', 'toastMessages.errorTitle');
        }

        this.acceptOrderState.set(false);
      },
      complete: () => null,
    });
  }
}
